/* eslint-disable react/prop-types */
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Swal from 'sweetalert2';
import { formatDateDDMMYYYY } from '../../utils/dateUtils';
import {
  Grid,
  Card,
  TextField,
  Button,
  Box,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Menu,
  MenuList,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDDatePicker from "components/MDDatePicker";
import LoaderOverlay from "components/LoaderOverlay";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import { api } from "../../api";

function PrescriptionList() {
  const navigate = useNavigate();

  // State management
  const [loading, setLoading] = useState(false);
  const [prescriptions, setPrescriptions] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedPrescription, setSelectedPrescription] = useState(null);

  // Load prescriptions data
  useEffect(() => {
    loadPrescriptions();
  }, []);

  const loadPrescriptions = async () => {
    setLoading(true);
    try {
      const searchParams = {
        search: searchText,
        status: statusFilter !== "ALL" ? statusFilter : undefined,
        type: typeFilter !== "ALL" ? typeFilter : undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
      };

      // Remove undefined values
      const filteredParams = Object.fromEntries(
        Object.entries(searchParams).filter(([_, v]) => v !== undefined)
      );

      const response = await api.opd.searchPrescriptions(filteredParams);
      
      if (response?.data) {
        setPrescriptions(response.data);
      } else {
        setPrescriptions([]);
      }
    } catch (error) {
      console.error("Error loading prescriptions:", error);
      await Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: 'Failed to load prescriptions. Please try again.',
        showConfirmButton: true,
        confirmButtonColor: '#d33',
        confirmButtonText: 'OK'
      });
      setPrescriptions([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle search
  const handleSearch = () => {
    loadPrescriptions();
  };

  // Clear all filters
  const handleClearFilters = () => {
    setSearchText("");
    setStatusFilter("ALL");
    setTypeFilter("ALL");
    setStartDate("");
    setEndDate("");
    // Reload with cleared filters
    setTimeout(() => {
      loadPrescriptions();
    }, 100);
  };

  // Status color mapping
  const getStatusColor = (status) => {
    switch (status) {
      case "DRAFT":
        return "default";
      case "PUBLISHED":
        return "success";
      case "RECALLED":
        return "error";
      case "REPUBLISHED":
        return "warning";
      default:
        return "default";
    }
  };

  // Action menu handlers
  const handleActionClick = (event, prescription) => {
    setAnchorEl(event.currentTarget);
    setSelectedPrescription(prescription);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedPrescription(null);
  };

  const handleViewPrescription = () => {
    if (selectedPrescription) {
      navigate(`/prescription/view/${selectedPrescription.id}`);
    }
    handleCloseMenu();
  };

  const handleEditPrescription = () => {
    if (selectedPrescription) {
      navigate(`/prescription/edit/${selectedPrescription.id}`);
    }
    handleCloseMenu();
  };

  const handleDeletePrescription = async () => {
    if (!selectedPrescription) return;

    const result = await Swal.fire({
      title: 'Delete Prescription?',
      text: `Are you sure you want to delete prescription ${selectedPrescription.prescription_code}? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#00798E',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        setLoading(true);
        await api.opd.deletePrescription(selectedPrescription.id);
        
        await Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: 'Prescription has been deleted successfully.',
          showConfirmButton: true,
          confirmButtonColor: '#00798E',
          confirmButtonText: 'OK'
        });

        // Reload the prescriptions list
        loadPrescriptions();
      } catch (error) {
        console.error("Error deleting prescription:", error);
        await Swal.fire({
          icon: 'error',
          title: 'Error!',
          text: 'Failed to delete prescription. Please try again.',
          showConfirmButton: true,
          confirmButtonColor: '#d33',
          confirmButtonText: 'OK'
        });
      } finally {
        setLoading(false);
      }
    }
    handleCloseMenu();
  };

  // Table columns configuration
  const columns = [
    {
      Header: "Prescription Code",
      accessor: "prescription_code",
      Cell: ({ value }) => (
        <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          {value}
        </Typography>
      ),
    },
    {
      Header: "Patient Name",
      accessor: "patient_name",
      Cell: ({ value }) => (
        <Typography variant="body2">
          {value || 'N/A'}
        </Typography>
      ),
    },
    {
      Header: "Doctor",
      accessor: "doctor_name",
      Cell: ({ value }) => (
        <Typography variant="body2">
          {value || 'N/A'}
        </Typography>
      ),
    },
    {
      Header: "Status",
      accessor: "status",
      Cell: ({ value }) => (
        <Chip
          label={value}
          color={getStatusColor(value)}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      Header: "Type",
      accessor: "type",
      Cell: ({ value }) => (
        <Chip
          label={value}
          color="primary"
          size="small"
          variant="filled"
          sx={{ minWidth: 60 }}
        />
      ),
    },
    {
      Header: "Date",
      accessor: "created_at",
      Cell: ({ value }) => (
        <Typography variant="body2">
          {formatDateDDMMYYYY(value)}
        </Typography>
      ),
    },
    {
      Header: "Actions",
      accessor: "actions",
      Cell: ({ row }) => (
        <IconButton
          size="small"
          onClick={(e) => handleActionClick(e, row.original)}
          sx={{ color: 'text.secondary' }}
        >
          <MoreVertIcon />
        </IconButton>
      ),
    },
  ];

  // Table data with actions
  const tableData = prescriptions.map((prescription) => ({
    ...prescription,
    actions: prescription.id, // This will be handled by the Cell component
  }));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <LoaderOverlay open={loading} />
      
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="logo"
                borderRadius="lg"
                coloredShadow="logo"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  Prescription List
                </MDTypography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => navigate("/patient-list")}
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    border: 'none',
                    borderRadius: 4,
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    padding: '8px 16px',
                  }}
                  onMouseOver={(e) => e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.3)'}
                  onMouseOut={(e) => e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'}
                >
                  Create New Prescription
                </Button>
              </MDBox>

              {/* Search and Filter Section */}
              <MDBox p={3}>
                <Grid container spacing={3} alignItems="center">
                  {/* Search Text */}
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Search Prescriptions"
                      placeholder="Search by code, patient, doctor..."
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      variant="outlined"
                      size="small"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleSearch();
                        }
                      }}
                    />
                  </Grid>

                  {/* Status Filter */}
                  <Grid item xs={6} md={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={statusFilter}
                        label="Status"
                        onChange={(e) => setStatusFilter(e.target.value)}
                      >
                        <MenuItem value="ALL">All Status</MenuItem>
                        <MenuItem value="DRAFT">Draft</MenuItem>
                        <MenuItem value="PUBLISHED">Published</MenuItem>
                        <MenuItem value="RECALLED">Recalled</MenuItem>
                        <MenuItem value="REPUBLISHED">Republished</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Type Filter */}
                  <Grid item xs={6} md={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Type</InputLabel>
                      <Select
                        value={typeFilter}
                        label="Type"
                        onChange={(e) => setTypeFilter(e.target.value)}
                      >
                        <MenuItem value="ALL">All Types</MenuItem>
                        <MenuItem value="OPD">OPD</MenuItem>
                        <MenuItem value="IPD">IPD</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Date Range */}
                  <Grid item xs={6} md={2}>
                    <MDDatePicker
                      label="Start Date"
                      value={startDate}
                      onChange={(date) => setStartDate(date)}
                      textFieldProps={{
                        fullWidth: true,
                        size: "small",
                      }}
                    />
                  </Grid>

                  <Grid item xs={6} md={2}>
                    <MDDatePicker
                      label="End Date"
                      value={endDate}
                      onChange={(date) => setEndDate(date)}
                      textFieldProps={{
                        fullWidth: true,
                        size: "small",
                      }}
                    />
                  </Grid>

                  {/* Action Buttons */}
                  <Grid item xs={12} md={1}>
                    <Box display="flex" gap={1}>
                      <Button
                        variant="contained"
                        startIcon={<SearchIcon />}
                        onClick={handleSearch}
                        size="small"
                        sx={{
                          backgroundColor: "#00798E",
                          "&:hover": { backgroundColor: "#005f6b" },
                        }}
                      >
                        Search
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<ClearIcon />}
                        onClick={handleClearFilters}
                        size="small"
                        sx={{
                          color: "#00798E",
                          borderColor: "#00798E",
                          "&:hover": { 
                            borderColor: "#005f6b",
                            backgroundColor: "rgba(0, 121, 142, 0.1)"
                          },
                        }}
                      >
                        Clear
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </MDBox>

              {/* Results Table */}
              <MDBox pb={3} px={3}>
                {prescriptions.length > 0 ? (
                  <DataTable
                    table={{ columns, rows: tableData }}
                    isSorted={false}
                    entriesPerPage={true}
                    showTotalEntries={true}
                    noEndBorder
                  />
                ) : (
                  <Box textAlign="center" py={6}>
                    <Typography variant="h6" color="textSecondary" gutterBottom>
                      No prescriptions found
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                      Try adjusting your search criteria or create a new prescription.
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => navigate("/patient-list")}
                      sx={{
                        backgroundColor: "#00798E",
                        "&:hover": { backgroundColor: "#005f6b" },
                      }}
                    >
                      Create New Prescription
                    </Button>
                  </Box>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        PaperProps={{
          sx: { width: 200 }
        }}
      >
        <MenuList>
          <MenuItem onClick={handleViewPrescription}>
            <ListItemIcon>
              <ViewIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>View Prescription</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleEditPrescription}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit Prescription</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleDeletePrescription} sx={{ color: 'error.main' }}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Delete Prescription</ListItemText>
          </MenuItem>
        </MenuList>
      </Menu>

      <Footer />
    </DashboardLayout>
  );
}

export default PrescriptionList;
